import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { reviewsApi } from '@/lib/api/reviews.api';

import type { CampaignReview, CallReview } from '@/lib/api/reviews.api';

export type { CampaignReview, CallReview };

export function useStartReview() {
  return useMutation({
    mutationFn: ({
      campaignId,
      unreviewedOnly,
      reviewConfigId,
    }: {
      campaignId: string;
      unreviewedOnly?: boolean;
      reviewConfigId?: string;
    }) => reviewsApi.startCampaignReview(campaignId, { unreviewedOnly, reviewConfigId }),
    retry: false,
  });
}

export function useCampaignReview(reviewId: string | undefined) {
  return useQuery<CampaignReview>({
    queryKey: ['campaign-review', reviewId],
    queryFn: () => reviewsApi.getCampaignReview(reviewId!),
    enabled: !!reviewId,
    refetchInterval: (query) => {
      // Poll every 3 seconds if review is in progress
      const data = query.state.data;
      return data?.status === 'IN_PROGRESS' || data?.status === 'PENDING' ? 3000 : false;
    },
  });
}

export function useCampaignReviews(campaignId: string | undefined) {
  return useQuery<CampaignReview[]>({
    queryKey: ['campaign-reviews', campaignId],
    queryFn: () => reviewsApi.getCampaignReviews(campaignId!),
    enabled: !!campaignId,
  });
}

export function useStopReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reviewId: string) => reviewsApi.stopCampaignReview(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-review'] });
    },
  });
}

// Individual call review hooks
export function useStartCallReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ callId, reviewConfigId }: { callId: string; reviewConfigId?: string }) =>
      reviewsApi.startCallReview(callId, { reviewConfigId }),
    onSuccess: (_, { callId }) => {
      // Invalidate call reviews for this call
      queryClient.invalidateQueries({ queryKey: ['call-reviews', callId] });
    },
  });
}

export function useCallReviews(callId: string | undefined) {
  return useQuery<CallReview[]>({
    queryKey: ['call-reviews', callId],
    queryFn: () => reviewsApi.getCallReviewsByCallId(callId!),
    enabled: !!callId,
    refetchInterval: (query) => {
      // Poll every 3 seconds if any review is in progress
      const data = query.state.data;
      const hasInProgress = data?.some(
        (review) => review.review_status === 'IN_PROGRESS' || review.review_status === 'PENDING',
      );
      return hasInProgress ? 3000 : false;
    },
  });
}
